import React, { useState, useEffect, useCallback } from 'react';
import { Delete, Warning } from '@mui/icons-material';
import { useStore } from 'zustand';
import { AttributeValue } from '../../../api/protosCompiled/globalItem/globalItem_pb';
import { useGetDefaultQuoteExpenseTypes } from '../../../hooks/useDefaultCloseoutHooks';
import { useFetchItemsByFileId } from '../../../hooks/useFetchItemsByFileId';
import { EstimateQuoteGroup } from '../../../api/protosCompiled/estimateAssembly/estimateAssembly_pb';
import { ChangeOrderQuoteGroup } from '../../../api/protosCompiled/changeOrderAssembly/changeOrderAssembly_pb';
import { IconButton, Tooltip, useTheme as useMuiTheme, alpha } from '@mui/material';
import {
  TakeoffAssemblyItem,
  TakeoffVariableType,
  takeoffVariableTypes,
} from '../../../types/AssemblyItemType';
import {
  GlobalAssemblyItem,
  GlobalQuoteGroup,
} from '../../../api/protosCompiled/globalAssembly/globalAssembly_pb';
import { useAssemblyStore } from '../../../stores/AssemblyStore';
import { useTheme } from '../../../contexts/ThemeContext';
import { isValidTakeoffVariable } from '../assemblies/CreateAssembly';
import { StyledTableCell } from '../../../styles/styles';
import { StyledTableRow } from '../../takeoffPallette/styles';
import { sortAttributes } from './ItemTable';
import { useFoundrySelectionStore } from '../../../stores/foundrySelection.store';
import { useAssemblyRowActions } from '../../accessManager';

export interface AddedItemRowProps {
  item: GlobalAssemblyItem.AsObject | TakeoffAssemblyItem;
  index: number;
}

type QuotGroupUnion =
  | GlobalQuoteGroup.AsObject
  | EstimateQuoteGroup.AsObject
  | ChangeOrderQuoteGroup.AsObject
  | null;

/**
 * The item & index passed to this component is only for adding items to the assembly
 * all other existing rows will get their information from the global / current assembly
 */
export const AddedItemRow = React.memo(({ item, index }: AddedItemRowProps) => {
  const muiTheme = useMuiTheme();
  const { darkMode } = useTheme();
  const currentGlobalAssembly = useStore(useAssemblyStore).currentGlobalAssembly;
  const currentTakeoffAssembly = useStore(useAssemblyStore).currentTakeoffAssembly;

  // Get the current assembly and the specific item at this index
  const assembly = currentGlobalAssembly || currentTakeoffAssembly;

  const setCurrentGlobalAssembly = useStore(useAssemblyStore).setCurrentGlobalAssembly;
  const setCurrentTakeoffAssembly = useStore(useAssemblyStore).setCurrentTakeoffAssembly;

  const { data: defaultQuoteGroups } = useGetDefaultQuoteExpenseTypes();
  const { data: items } = useFetchItemsByFileId(item.itemid);

  const [isQuoted, setIsQuoted] = useState<boolean>(item.isquoted || false);
  const [selectedAttributeValueId, setSelectedAttributeValueId] = useState<string>('');
  const [quantity, setQuantity] = useState<number>(item.itemqtyformula?.itemqty || 1);
  const [forEvery, setForEvery] = useState<number>(item.itemqtyformula?.forevery || 1);
  const [quantityDisplay, setQuantityDisplay] = useState<string>(String(quantity));
  const [forEveryDisplay, setForEveryDisplay] = useState<string>(String(forEvery));
  const defaultTakeoffVariable: TakeoffVariableType = 'point';
  const [takeoffVariable, setTakeoffVariable] = useState<string>(
    item.itemqtyformula?.takeoffvariabletype || defaultTakeoffVariable
  );
  const [quoteGroup, setQuoteGroup] = useState<QuotGroupUnion>(null);

  // Foundry-specific state - check stored path first, then fallback to selection store
  const storedCategoryPath = (item as any)?.foundrycategorypath;
  const itemIdToCategoryPath = useFoundrySelectionStore(state => state.itemIdToCategoryPath);
  const foundryCategoryPath = storedCategoryPath || itemIdToCategoryPath[(item as any)?.itemid];
  const getOptionsForCategory = useFoundrySelectionStore(state => state.getOptionsForCategory);
  const setOptionsForCategory = useFoundrySelectionStore(state => state.setOptionsForCategory);
  const isFoundryItem =
    (item as any)?.isfoundryitem === true ||
    !!foundryCategoryPath ||
    (item as any)?.attribute?.attributevaluename?.includes('Foundry Item');
  const [foundryOptions, setFoundryOptions] = useState<{ id: string; name: string }[]>([]);
  const [selectedFoundryItemId, setSelectedFoundryItemId] = useState<string>(
    (item as any)?.itemid || ''
  );

  const { handleDisableRowActions } = useAssemblyRowActions();

  // Keep selectedFoundryItemId in sync with current row item
  useEffect(() => {
    if (isFoundryItem && (item as any)?.itemid) {
      setSelectedFoundryItemId((item as any).itemid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(item as any)?.itemid, isFoundryItem]);

  // Add state to track if inputs are being edited
  const [isEditingQuantity, setIsEditingQuantity] = useState<boolean>(false);
  const [isEditingForEvery, setIsEditingForEvery] = useState<boolean>(false);

  // Format number for display while preserving precision in state
  const formatNumberForDisplay = (num: number): string => {
    // Handle integers
    if (Number.isInteger(num)) {
      return num.toString();
    }

    // For decimals, show at most 2 decimal places
    // Use toFixed(2) to get 2 decimal places, then remove trailing zeros
    return Number(num.toFixed(2)).toString();
  };

  // Update display values when the actual values change
  useEffect(() => {
    // Only update displays if the user isn't currently editing the field
    if (!isEditingQuantity) {
      setQuantityDisplay(formatNumberForDisplay(quantity));
    }
    if (!isEditingForEvery) {
      setForEveryDisplay(formatNumberForDisplay(forEvery));
    }
  }, [quantity, forEvery, isEditingQuantity, isEditingForEvery]);

  // Initialize with formatted display values only when item ID or formula changes
  useEffect(() => {
    if (item.itemqtyformula?.itemqty) {
      setQuantityDisplay(formatNumberForDisplay(item.itemqtyformula.itemqty));
      setQuantity(item.itemqtyformula.itemqty);
    }
    if (item.itemqtyformula?.forevery) {
      setForEveryDisplay(formatNumberForDisplay(item.itemqtyformula.forevery));
      setForEvery(item.itemqtyformula.forevery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.itemid, item.itemqtyformula?.formulaid]);

  // Update formulas in currentGlobalAssembly or currentTakeoffAssembly
  useEffect(() => {
    // Skip this update if either input field is being edited
    if (isEditingQuantity || isEditingForEvery) {
      return;
    }

    const updateAssemblyItems = (assembly: any, setAssembly: any) => {
      const updatedFormulas = assembly?.itemsList?.map((formula: any, idx: any) => {
        if (idx === index) {
          const attributesList: AttributeValue.AsObject[] =
            items && items.length > 0 ? items[0].attributesList : [];
          const selectedAttribute = attributesList.find(
            attr => attr.attributevalueid.toString() === selectedAttributeValueId
          );

          return {
            ...formula,
            itemname: item.itemname,
            attribute: selectedAttribute || formula.attribute,
            isquoted: isQuoted,
            itemqtyformula: {
              ...formula.itemqtyformula,
              itemqty: quantity,
              forevery: forEvery,
              takeoffvariabletype: takeoffVariable,
              itemid: formula.itemid,
              isquoted: formula.isquoted || false,
              attributevalueid: selectedAttribute?.attributevalueid || 0,
              quotegroupid: selectedAttribute?.attributegroupid || '',
            },
            quotegroup: {
              id: quoteGroup?.id || '',
              quotegroup: quoteGroup?.quotegroup || '',
            },
          };
        }
        return formula;
      });

      if (assembly && updatedFormulas) {
        setAssembly({
          ...assembly,
          itemsList: updatedFormulas,
        });
      }
    };

    if (currentGlobalAssembly) {
      updateAssemblyItems(currentGlobalAssembly, setCurrentGlobalAssembly);
    } else if (currentTakeoffAssembly) {
      updateAssemblyItems(currentTakeoffAssembly, setCurrentTakeoffAssembly);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedAttributeValueId,
    isQuoted,
    quantity,
    forEvery,
    takeoffVariable,
    quoteGroup,
    items,
    isEditingQuantity,
    isEditingForEvery,
  ]);

  // Initialize the state with the item values on mount or when assembly changes
  useEffect(() => {
    if (item.attribute) {
      // Set selectedAttributeValueId to the attributevalueid of the current attribute
      setSelectedAttributeValueId(item.attribute.attributevalueid.toString());
    } else if (items && items.length > 0 && items[0].attributesList.length > 0) {
      // If item doesn't have attributes, use the first attribute from items[0]
      setSelectedAttributeValueId(items[0].attributesList[0].attributevalueid.toString());
    }
    setQuantity(item.itemqtyformula?.itemqty || 1);
    setForEvery(item.itemqtyformula?.forevery || 1);
    setTakeoffVariable(item.itemqtyformula?.takeoffvariabletype || 'point');
    setIsQuoted(item.isquoted || false);
    setQuoteGroup(item?.quotegroup || null);

    // If using an assembly item, get it directly
    if (assembly?.itemsList?.[index]) {
      setIsQuoted(assembly.itemsList[index].isquoted || false);
      setQuoteGroup(assembly.itemsList[index].quotegroup || null);
      // Also load values from the assembly item formula when available
      if (assembly.itemsList[index].itemqtyformula) {
        setQuantity(assembly.itemsList[index].itemqtyformula?.itemqty || 1);
        setForEvery(assembly.itemsList[index].itemqtyformula?.forevery || 1);
        setTakeoffVariable(
          assembly.itemsList[index].itemqtyformula?.takeoffvariabletype || 'point'
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.itemid, assembly?.assemblyid, items]);

  // Initialize foundry options with current item if it's a foundry item
  useEffect(() => {
    if (isFoundryItem && foundryOptions.length === 0) {
      const currentItemOption = { id: (item as any)?.itemid, name: (item as any)?.itemname };
      setFoundryOptions([currentItemOption] as any);
    }
  }, [isFoundryItem, foundryOptions.length, item]);

  // Check for preloaded options when component mounts or foundry item changes
  useEffect(() => {
    if (!isFoundryItem) return;

    const path = foundryCategoryPath;

    if (path) {
      const cached = getOptionsForCategory(path);
      if (cached && cached.length > 0) {
        // Ensure the current item is always included in the options
        const currentItemOption = { id: (item as any)?.itemid, name: (item as any)?.itemname };
        const hasCurrent = cached.some(opt => opt.id === currentItemOption.id);
        const finalOptions = hasCurrent ? cached : [currentItemOption, ...cached];
        setFoundryOptions(finalOptions as any);
      }
    } else {
      // No category path available, just show the current item
      const currentItemOption = { id: (item as any)?.itemid, name: (item as any)?.itemname };
      setFoundryOptions([currentItemOption] as any);
    }
  }, [isFoundryItem, foundryCategoryPath, item, getOptionsForCategory]);

  // Load foundry options for foundry items
  useEffect(() => {
    const loadFoundryOptions = async () => {
      if (!isFoundryItem) return;

      const path = foundryCategoryPath;

      // If we don't have a category path, just show the current item
      if (!path) {
        const currentItemOption = { id: (item as any)?.itemid, name: (item as any)?.itemname };
        setFoundryOptions([currentItemOption] as any);
        return;
      }

      try {
        // First check if we have cached options (preloaded)
        const cached = getOptionsForCategory(path);
        if (cached && cached.length) {
          // Ensure the current item is always included in the options
          const currentItemOption = { id: (item as any)?.itemid, name: (item as any)?.itemname };
          const hasCurrent = cached.some(opt => opt.id === currentItemOption.id);
          const finalOptions = hasCurrent ? cached : [currentItemOption, ...cached];
          setFoundryOptions(finalOptions as any);
          return;
        }

        // Load from gRPC API
        const { BuildFoundryService } = await import('../../../api/BuildFoundryService');
        const res = await BuildFoundryService.getFoundryItemsByCategoryPath(path);
        const options = (res.dataList || []).map((x: any) => ({ id: x.id, name: x.name }));

        // Ensure the current item is always included in the options
        const currentItemOption = { id: (item as any)?.itemid, name: (item as any)?.itemname };
        const hasCurrent = options.some(opt => opt.id === currentItemOption.id);
        const finalOptions = hasCurrent ? options : [currentItemOption, ...options];

        setFoundryOptions(finalOptions as any);
        setOptionsForCategory(path, finalOptions);
      } catch (err) {
        console.error('Failed to load foundry items for category:', path, err);
        // Fallback: ensure we at least have the current item in the options
        const currentItemOption = { id: (item as any)?.itemid, name: (item as any)?.itemname };
        setFoundryOptions([currentItemOption] as any);
        // Don't cache failed requests to avoid repeated API calls
      }
    };
    loadFoundryOptions();
  }, [isFoundryItem, foundryCategoryPath, getOptionsForCategory, setOptionsForCategory, item]);

  const handleQuoteGroupChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      e.stopPropagation();
      e.preventDefault();

      const selectedGroup = defaultQuoteGroups?.find(group => group.quotegroup === e.target.value);

      if (!selectedGroup) {
        console.warn('Selected quote group not found in defaultQuoteGroups.');
        return;
      }

      setQuoteGroup(selectedGroup);
    },
    [defaultQuoteGroups]
  );

  // Handle isQuoted change
  const handleIsQuotedChange = useCallback(() => {
    const newQuotedState = !isQuoted;
    setIsQuoted(newQuotedState);

    const toggleQuotedState = (assembly: any, setAssembly: any) => {
      const updatedFormulas = assembly?.itemsList?.map((item: any, idx: number) => {
        if (idx === index) {
          return {
            ...item,
            isquoted: newQuotedState,
            // If not quoted, clear the quote group
            quotegroup: newQuotedState ? item.quotegroup : null,
          };
        }
        return item;
      });

      if (assembly && updatedFormulas) {
        setAssembly({
          ...assembly,
          itemsList: updatedFormulas,
        });
      }
    };

    if (currentGlobalAssembly) {
      toggleQuotedState(currentGlobalAssembly, setCurrentGlobalAssembly);
    } else if (currentTakeoffAssembly) {
      toggleQuotedState(currentTakeoffAssembly, setCurrentTakeoffAssembly);
    }
  }, [
    currentGlobalAssembly,
    currentTakeoffAssembly,
    index,
    isQuoted,
    setCurrentGlobalAssembly,
    setCurrentTakeoffAssembly,
  ]);

  const handleDeleteAssemblyItem = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();
      if (currentGlobalAssembly) {
        setCurrentGlobalAssembly((prevAssembly: any) => {
          const updatedFormulas = prevAssembly?.itemsList?.filter(
            (_: any, idx: number) => idx !== index
          );

          return {
            ...prevAssembly,
            itemsList: updatedFormulas,
          };
        });
      } else if (currentTakeoffAssembly) {
        setCurrentTakeoffAssembly((prevAssembly: any) => {
          const updatedFormulas = prevAssembly?.itemsList?.filter(
            (_: any, idx: number) => idx !== index
          );

          return {
            ...prevAssembly,
            itemsList: updatedFormulas,
          };
        });
      }
    },
    [
      currentGlobalAssembly,
      currentTakeoffAssembly,
      index,
      setCurrentGlobalAssembly,
      setCurrentTakeoffAssembly,
    ]
  );

  // Sort and group attributes by their names
  const getSortedAttributes = useCallback((attributes: AttributeValue.AsObject[]) => {
    // Use the same sorting function as ItemTable to ensure consistency
    return [...attributes].sort(sortAttributes);
  }, []);

  // Get current measurement type
  const currentMeasurementType =
    currentGlobalAssembly?.assemblymeasurementtype ||
    currentTakeoffAssembly?.assemblymeasurementtype;

  // Check if the current takeoff variable is valid
  const isCurrentVariableValid = isValidTakeoffVariable(
    takeoffVariable,
    currentMeasurementType || ''
  );

  return (
    <StyledTableRow id={`added-item-row-${index}`} sx={{ position: 'relative' }}>
      {/* isQuoted Checkbox */}
      <StyledTableCell>
        <input
          disabled={handleDisableRowActions()}
          type="checkbox"
          checked={isQuoted}
          onChange={handleIsQuotedChange}
          style={{
            cursor: 'pointer',
            width: '16px',
            height: '16px',
            accentColor: muiTheme.palette.primary.main,
          }}
        />
      </StyledTableCell>

      {/* Item/Description Select */}
      <StyledTableCell>
        <div style={{ position: 'relative', width: '100%', zIndex: 100, overflow: 'visible' }}>
          {/* If foundry item, show foundry item select */}
          {isFoundryItem ? (
            <select
              disabled={handleDisableRowActions()}
              value={selectedFoundryItemId}
              onChange={e => {
                e.stopPropagation();
                e.preventDefault();
                const newId = e.target.value;
                setSelectedFoundryItemId(newId);
                // update current assembly item id/name when switching foundry item
                const next = foundryOptions.find(opt => opt.id === newId);
                if (next) {
                  // First update the item immediately with available info
                  const updateImmediate = (prev: any) => {
                    if (!prev) return prev;
                    const updated = { ...prev } as any;
                    if (updated.itemsList && updated.itemsList[index]) {
                      updated.itemsList[index] = {
                        ...updated.itemsList[index],
                        itemid: next.id,
                        itemname: next.name,
                        isfoundryitem: true,
                        foundrycategorypath: foundryCategoryPath || '',
                      };
                    }
                    return updated;
                  };

                  if (currentGlobalAssembly) {
                    setCurrentGlobalAssembly(updateImmediate);
                  } else if (currentTakeoffAssembly) {
                    setCurrentTakeoffAssembly(updateImmediate);
                  }

                  // Then fetch and update the category path asynchronously
                  (async () => {
                    try {
                      const { BuildFoundryService } = await import(
                        '../../../api/BuildFoundryService'
                      );
                      const itemDetails = await BuildFoundryService.getFoundryItemById(newId);
                      if (itemDetails.data?.categoryId) {
                        console.log(
                          `🔧 Updating category path for switched item ${next.name}: ${itemDetails.data.categoryId}`
                        );

                        const updateWithCategoryPath = (prev: any) => {
                          if (!prev) return prev;
                          const updated = { ...prev } as any;
                          if (updated.itemsList && updated.itemsList[index]) {
                            updated.itemsList[index] = {
                              ...updated.itemsList[index],
                              foundrycategorypath: itemDetails.data?.categoryId,
                            };
                          }
                          return updated;
                        };

                        if (currentGlobalAssembly) {
                          setCurrentGlobalAssembly(updateWithCategoryPath);
                        } else if (currentTakeoffAssembly) {
                          setCurrentTakeoffAssembly(updateWithCategoryPath);
                        }
                      }
                    } catch (err) {
                      console.warn(`Failed to fetch category path for item ${newId}:`, err);
                    }
                  })();
                }
              }}
              onClick={e => e.stopPropagation()}
              style={{
                width: '100%',
                backgroundColor: darkMode ? '#333' : 'white',
                color: muiTheme.palette.text.primary,
                padding: '8px 30px 8px 12px',
                border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)'}`,
                cursor: 'pointer',
                outline: 'none',
                appearance: 'menulist',
                WebkitAppearance: 'menulist',
                MozAppearance: 'menulist',
                fontSize: '14px',
                lineHeight: '1.4',
                position: 'relative',
                zIndex: 100,
                textOverflow: 'ellipsis',
              }}
            >
              {/* Ensure the current item appears in the list */}
              {(() => {
                // Always ensure we have at least the current item
                const currentItemOption = {
                  id: (item as any)?.itemid,
                  name: (item as any)?.itemname,
                };
                const hasCurrent = foundryOptions.some(opt => opt.id === currentItemOption.id);
                const list = hasCurrent
                  ? foundryOptions
                  : [currentItemOption].concat(foundryOptions);
                return list.map((opt: any) => (
                  <option key={opt.id} value={opt.id}>
                    {'🌍 ' + opt.name}
                  </option>
                ));
              })()}
            </select>
          ) : (
            <select
              disabled={handleDisableRowActions()}
              value={selectedAttributeValueId}
              onChange={e => {
                e.stopPropagation();
                e.preventDefault();
                setSelectedAttributeValueId(e.target.value);
              }}
              onClick={e => {
                e.stopPropagation();
              }}
              style={{
                width: '100%',
                backgroundColor: darkMode ? '#333' : 'white',
                color: muiTheme.palette.text.primary,
                padding: '8px 30px 8px 12px',
                border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)'}`,
                cursor: 'pointer',
                outline: 'none',
                appearance: 'menulist',
                WebkitAppearance: 'menulist',
                MozAppearance: 'menulist',
                fontSize: '14px',
                lineHeight: '1.4',
                position: 'relative',
                zIndex: 100,
                textOverflow: 'ellipsis',
              }}
            >
              {items &&
                items.length > 0 &&
                getSortedAttributes(items[0].attributesList)?.map(attr => (
                  <option
                    key={attr.attributevalueid}
                    value={attr.attributevalueid.toString()}
                    style={{
                      backgroundColor: darkMode ? '#333' : 'white',
                      color: muiTheme.palette.text.primary,
                      padding: '8px',
                    }}
                  >
                    {generateAttributeLabel(attr, item)}
                  </option>
                ))}
            </select>
          )}
        </div>

        {/* Conditional quoteGroup selection */}
        {isQuoted && defaultQuoteGroups && (
          <div style={{ marginTop: '5px' }}>
            <label htmlFor={`quoteGroup-${index}`} style={{ color: muiTheme.palette.text.primary }}>
              Quote Group:
            </label>
            <select
              disabled={handleDisableRowActions()}
              id={`quoteGroup-${index}`}
              value={quoteGroup?.quotegroup || ''}
              onChange={handleQuoteGroupChange}
              style={{
                width: '95%',
                backgroundColor: 'transparent',
                color: muiTheme.palette.text.primary,
                border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)'}`,
                padding: '8px',
                cursor: 'pointer',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = muiTheme.palette.primary.main;
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = darkMode
                  ? 'rgba(255, 255, 255, 0.23)'
                  : 'rgba(0, 0, 0, 0.23)';
              }}
              onFocus={e => {
                e.currentTarget.style.borderColor = muiTheme.palette.primary.main;
                e.currentTarget.style.outline = 'none';
              }}
              onBlur={e => {
                e.currentTarget.style.borderColor = darkMode
                  ? 'rgba(255, 255, 255, 0.23)'
                  : 'rgba(0, 0, 0, 0.23)';
              }}
            >
              {defaultQuoteGroups?.map(group => (
                <option
                  key={group.id || group.quotegroup}
                  value={group.quotegroup}
                  style={{
                    backgroundColor: muiTheme.palette.background.paper,
                    color: muiTheme.palette.text.primary,
                  }}
                >
                  {group.quotegroup}
                </option>
              ))}
            </select>
          </div>
        )}
      </StyledTableCell>

      {/* Quantity */}
      <StyledTableCell>
        <input
          disabled={handleDisableRowActions()}
          type="number"
          step="any"
          min="0"
          value={quantityDisplay}
          onClick={e => {
            e.stopPropagation();
            setIsEditingQuantity(true);
          }}
          onChange={e => {
            e.stopPropagation();
            setQuantityDisplay(e.target.value);
          }}
          onFocus={() => setIsEditingQuantity(true)}
          onBlur={e => {
            e.stopPropagation();
            const value = e.target.value;
            if (value === '') {
              setQuantity(0);
              setQuantityDisplay('0');
            } else {
              const numValue = parseFloat(value);
              if (!isNaN(numValue)) {
                setQuantity(numValue);
                // Don't update display here since we want to preserve user input
              }
            }
            // Use setTimeout to ensure the quantity state is updated before turning off editing
            setTimeout(() => setIsEditingQuantity(false), 10);
          }}
          style={{
            width: '100%',
            backgroundColor: 'transparent',
            color: 'inherit',
            border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)'}`,
            padding: '8px',
            position: 'relative',
            zIndex: 10,
            pointerEvents: 'auto',
          }}
        />
      </StyledTableCell>

      {/* For every */}
      <StyledTableCell>
        <input
          disabled={handleDisableRowActions()}
          type="number"
          step="any"
          min="0"
          value={forEveryDisplay}
          onClick={e => {
            e.stopPropagation();
            setIsEditingForEvery(true);
          }}
          onChange={e => {
            e.stopPropagation();
            setForEveryDisplay(e.target.value);
          }}
          onFocus={() => setIsEditingForEvery(true)}
          onBlur={e => {
            e.stopPropagation();
            const value = e.target.value;
            if (value === '') {
              setForEvery(0);
              setForEveryDisplay('0');
            } else {
              const numValue = parseFloat(value);
              if (!isNaN(numValue)) {
                setForEvery(numValue);
                // Don't update display here since we want to preserve user input
              }
            }
            // Use setTimeout to ensure the forEvery state is updated before turning off editing
            setTimeout(() => setIsEditingForEvery(false), 10);
          }}
          style={{
            width: '100%',
            backgroundColor: 'transparent',
            color: 'inherit',
            border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)'}`,
            padding: '8px',
            position: 'relative',
            zIndex: 10,
            pointerEvents: 'auto',
          }}
        />
      </StyledTableCell>

      {/* Takeoff variable */}
      <StyledTableCell>
        <div style={{ position: 'relative', width: '100%' }}>
          <select
            disabled={handleDisableRowActions()}
            value={takeoffVariable}
            onChange={e => setTakeoffVariable(e.target.value)}
            style={{
              width: '100%',
              backgroundColor: isCurrentVariableValid
                ? 'transparent'
                : alpha(muiTheme.palette.error.light, 0.1),
              color: muiTheme.palette.text.primary,
              border: `1px solid ${
                !isCurrentVariableValid
                  ? muiTheme.palette.error.main
                  : darkMode
                    ? 'rgba(255, 255, 255, 0.23)'
                    : 'rgba(0, 0, 0, 0.23)'
              }`,
              padding: '8px',
              cursor: 'pointer',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = isCurrentVariableValid
                ? muiTheme.palette.primary.main
                : muiTheme.palette.error.main;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = isCurrentVariableValid
                ? darkMode
                  ? 'rgba(255, 255, 255, 0.23)'
                  : 'rgba(0, 0, 0, 0.23)'
                : muiTheme.palette.error.main;
            }}
            onFocus={e => {
              e.currentTarget.style.borderColor = isCurrentVariableValid
                ? muiTheme.palette.primary.main
                : muiTheme.palette.error.main;
              e.currentTarget.style.outline = 'none';
            }}
            onBlur={e => {
              e.currentTarget.style.borderColor = isCurrentVariableValid
                ? darkMode
                  ? 'rgba(255, 255, 255, 0.23)'
                  : 'rgba(0, 0, 0, 0.23)'
                : muiTheme.palette.error.main;
            }}
          >
            {/* Always include the current value, even if invalid */}
            {!isCurrentVariableValid && !takeoffVariableTypes.includes(takeoffVariable as any) && (
              <option
                value={takeoffVariable}
                style={{
                  backgroundColor: alpha(muiTheme.palette.error.light, 0.2),
                  color: muiTheme.palette.error.main,
                }}
              >
                {takeoffVariable} (invalid)
              </option>
            )}

            {/* Show all valid takeoff variables for the current measurement type */}
            {takeoffVariableTypes.map(type => {
              const isValid = isValidTakeoffVariable(type, currentMeasurementType || '');
              return (
                <option
                  key={type}
                  value={type}
                  style={{
                    backgroundColor: isValid
                      ? muiTheme.palette.background.paper
                      : alpha(muiTheme.palette.error.light, 0.2),
                    color: isValid ? muiTheme.palette.text.primary : muiTheme.palette.error.main,
                    fontStyle: !isValid ? 'italic' : 'normal',
                  }}
                >
                  {type}
                  {!isValid ? ' (invalid)' : ''}
                </option>
              );
            })}
          </select>

          {/* Warning icon for invalid takeoff variable */}
          {!isCurrentVariableValid && (
            <Tooltip
              title={`'${takeoffVariable}' is not valid for ${currentMeasurementType} assembly type. Valid types are: ${
                currentMeasurementType === 'count'
                  ? 'point'
                  : currentMeasurementType === 'length'
                    ? 'point, segment, length'
                    : 'all types'
              }`}
            >
              <Warning
                sx={{
                  position: 'absolute',
                  right: '-24px',
                  top: '8px',
                  color: muiTheme.palette.error.main,
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%': { opacity: 0.7 },
                    '50%': { opacity: 1 },
                    '100%': { opacity: 0.7 },
                  },
                  zIndex: 9999,
                  filter: 'drop-shadow(0px 0px 2px rgba(0,0,0,0.5))',
                  transform: 'translateZ(0)',
                }}
              />
            </Tooltip>
          )}
        </div>
      </StyledTableCell>

      {/* Delete Button */}
      <StyledTableCell>
        <Tooltip title="Delete item">
          <IconButton
            disabled={handleDisableRowActions()}
            onClick={handleDeleteAssemblyItem}
            sx={{
              color: muiTheme.palette.error.main,
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                backgroundColor: darkMode
                  ? alpha(muiTheme.palette.error.main, 0.1)
                  : alpha(muiTheme.palette.error.main, 0.05),
              },
            }}
            aria-label="Delete item"
          >
            <Delete />
          </IconButton>
        </Tooltip>
      </StyledTableCell>
    </StyledTableRow>
  );
});

// Generate the label for each attribute
export type AttributeValueName = Pick<AttributeValue.AsObject, 'attributevaluename'>;

export const generateAttributeLabel = (attr: AttributeValueName, item: { itemname: string }) => {
  const attributeName = attr.attributevaluename.trim();
  const itemName = item.itemname.trim();
  return `${attributeName} - ${itemName}`;
};
